import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import * as PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { connect } from 'react-redux';

import {
  fetchComments,
  changeUrls,
  resetUrls,
  changePagination,
  resetPagination
} from '@store/comments/actions';
import API from '@utils/API';
import Logger from '@utils/Logger';
import Helpers from '@utils/Helpers';
import Button from '@components/shared/Button';
import Pagination from '@components/shared/Pagination';
import CommentCard from '@components/shared/CommentCard';
import CommentsForm from '@components/shared/CommentsForm';
import LoaderWrapper from '@components/shared/LoaderWrapper';
import CommentsEmptyState from '@components/shared/CommentsList/EmptyState';
import CardProtect from '@components/Reviews/Card/Protect';

const CommentsList = ({
  getUrl,
  createUrl,
  className,
  commentsList,
  commentsLoading,
  pagination,
  fetchComments,
  changeUrls,
  resetUrls,
  changePagination,
  resetPagination,
  commentsIsVisible,
  currentUser
}) => {
  const router = useRouter();
  const [showComments, setShowComments] = useState(false);

  useEffect(() => {
    changeUrls({ getList: getUrl });
    fetchComments();

    return () => {
      resetUrls();
      resetPagination();
    };
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setShowComments(true);
      return;
    }
    setShowComments(
      commentsIsVisible ? true : Helpers.arrayHasItems(commentsList)
    );
  }, [commentsList]);

  const handleAddedComment = async (values, actions) => {
    try {
      await API.post(createUrl, values);
      changePagination({ page: 1 });
      fetchComments();

      actions.resetForm();
      actions.validateForm();
    } catch (err) {
      Logger.error(err);
      actions.setErrors(err);
    }
  };

  const handleChangePagination = paginator => {
    changePagination(paginator);
    fetchComments();
  };

  return (
    (showComments && (
      <div className={classNames('comments-list', className)}>
        <p className="comments-list__title">Comments</p>
        <div className="comments-list__form">
          {currentUser ? (
            <CommentsForm onSubmit={handleAddedComment} />
          ) : (
            <CardProtect
              guestRedirectUri={router.asPath}
              text="Join the CureRate community to leave your comments"
            />
          )}
        </div>

        <LoaderWrapper isLoading={commentsLoading}>
          <div className="comments-list__items">
            {commentsList.map(comment => (
              <CommentCard
                key={comment.id}
                comment={comment}
                className="comments-list__item"
                onRemoved={fetchComments}
              />
            ))}

            <Pagination
              key={pagination.page}
              pagination={pagination}
              disabled={commentsLoading}
              onChange={handleChangePagination}
            />
          </div>
        </LoaderWrapper>
      </div>
    )) || (
      <div className="comments-list__empty">
        <CommentsEmptyState size="sm" />
        <Button
          color="yellow"
          className="comments-list__btn"
          onClick={() => setShowComments(!showComments)}
        >
          Comment
        </Button>
      </div>
    )
  );
};

CommentsList.defaultProps = {
  commentsIsVisible: false
};

CommentsList.propTypes = {
  commentsIsVisible: PropTypes.bool,
  getUrl: PropTypes.string,
  createUrl: PropTypes.string,
  className: PropTypes.string,
  commentsList: PropTypes.array,
  commentsLoading: PropTypes.bool,
  pagination: PropTypes.object,
  fetchComments: PropTypes.func,
  changeUrls: PropTypes.func,
  resetUrls: PropTypes.func,
  changePagination: PropTypes.func,
  resetPagination: PropTypes.func,
  currentUser: PropTypes.any
};

const mapStateToProps = ({
  auth: { user },
  comments: { commentsList, commentsLoading, pagination }
}) => ({
  commentsList,
  commentsLoading,
  pagination,
  currentUser: user
});

export default connect(mapStateToProps, {
  fetchComments,
  changeUrls,
  resetUrls,
  changePagination,
  resetPagination
})(CommentsList);
